import React, { useEffect, useRef } from "react";
import { CheckCircle2, X } from "lucide-react";

/**
 * Fenêtre de confirmation après l'envoi du formulaire (demande du
 * 01/11/2026). Accessible : rôle dialog modal, titre relié, focus placé
 * dans la fenêtre et gardé dedans, Échap ou un clic sur le voile ferment,
 * le focus revient au bouton d'envoi, la page derrière ne défile pas.
 */
export const FenetreConfirmation = ({ nom, courriel, onFermer, retourFocus }) => {
  const bouton = useRef(null);
  const fenetre = useRef(null);

  useEffect(() => {
    const precedent = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    bouton.current?.focus();

    const surTouche = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onFermer();
        return;
      }
      if (e.key !== "Tab") return;
      const elements = fenetre.current?.querySelectorAll("button, a[href]") ?? [];
      const premier = elements[0];
      const dernier = elements[elements.length - 1];
      if (e.shiftKey && document.activeElement === premier) {
        e.preventDefault();
        dernier.focus();
      } else if (!e.shiftKey && document.activeElement === dernier) {
        e.preventDefault();
        premier.focus();
      }
    };
    document.addEventListener("keydown", surTouche);
    return () => {
      document.removeEventListener("keydown", surTouche);
      document.body.style.overflow = precedent;
      retourFocus?.current?.focus({ preventScroll: true });
    };
  }, [onFermer, retourFocus]);

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-brand/60 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onFermer();
      }}
    >
      <div
        ref={fenetre}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirmation-titre"
        aria-describedby="confirmation-texte"
        className="relative w-full max-w-[520px] rounded-[20px] border border-border bg-white p-7 text-center shadow-[0_12px_40px_rgba(8,61,68,0.25)] sm:p-9"
      >
        <button
          type="button"
          onClick={onFermer}
          aria-label="Fermer la fenêtre"
          title="Fermer"
          className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full text-t2"
        >
          <X className="h-5 w-5" strokeWidth={2.4} aria-hidden="true" />
        </button>

        <CheckCircle2
          className="mx-auto h-14 w-14 text-accent"
          strokeWidth={1.8}
          aria-hidden="true"
        />
        <h2 id="confirmation-titre" className="mt-4 font-display text-vt-h3 font-extrabold text-brand">
          Message envoyé
        </h2>
        <div id="confirmation-texte" className="mt-3 space-y-3 leading-relaxed text-t2">
          <p>
            Merci {nom}. Nous avons bien reçu votre demande et revenons vers
            vous <strong className="text-foreground">sous 24 h ouvrables</strong>.
          </p>
          <p>
            Un courriel de confirmation vient de partir à{" "}
            <strong className="break-words text-foreground">{courriel}</strong>.
            Pensez à vérifier vos courriels indésirables.
          </p>
        </div>
        <button ref={bouton} type="button" onClick={onFermer} className="btn-accent mt-7 w-full sm:w-auto">
          Fermer
        </button>
      </div>
    </div>
  );
};
